import { useTranslation } from 'react-i18next';

import { SidebarNav } from '@/shared/components/Layout/SidebarNav/SidebarNav';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/ui/Sheet/Sheet';

export interface MobileNavProps {
  /** Referenced by the topbar trigger's `aria-controls`. */
  id: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

/**
 * The navigation on a viewport too narrow to give it a permanent column.
 *
 * `Sheet` was built in P9 for exactly this and had no consumer until now, which
 * is also why the persistent rail was the only navigation surface at every width.
 * Being a Radix dialog underneath, it arrives with the focus trap, the scroll
 * lock and Escape handling already correct — none of which a hand-rolled
 * `translate-x` panel would have.
 *
 * `side="inline-start"` docks it to the edge the reader starts from, so it slides
 * in from the right in Arabic and the left in English with no conditional (U7).
 * The width is `w-sidebar` rather than the sheet's default `w-dialog-sm`, because
 * 420px is wider than the phone this exists for.
 */
export function MobileNav({ id, isOpen, onOpenChange }: MobileNavProps) {
  const { t } = useTranslation('uiKit');

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent id={id} side="inline-start" className="w-sidebar p-md lg:hidden">
        {/* Radix requires a title for the dialog's accessible name; it doubles as
            the drawer's heading, so it is shown rather than `sr-only`. */}
        <SheetHeader>
          <SheetTitle className="px-sm text-label font-medium">{t('shell.menu')}</SheetTitle>
        </SheetHeader>

        {/* The nav list scrolls INSIDE the body, by composition rather than by
            `SidebarNav` happening to carry the right three classes — which is
            how it worked until P10.1 and would have stopped working the first
            time the rail was reused somewhere that did not scroll. `-mx-md`
            matches this sheet's tighter `p-md` padding, undoing the body's own
            full-bleed offset for the wider default. */}
        <SheetBody className="-mx-md px-md">
          <SidebarNav isCollapsed={false} />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
